/**
 * SA Tools - Getting Started Component
 * File upload and bulk process import
 */
import React, { useState } from 'react';
import { Paper, Title, Text, FileButton, Button, Group, Stack, Alert } from '@mantine/core';
import { IconUpload, IconFileSpreadsheet } from '@tabler/icons-react';
import { parseCSV } from '../../utils/csvParser';
import { sortAndRenumberProcesses } from '../../utils/scoreCalculator';

function GettingStarted({ onFilesUploaded }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleFileUpload = async (file) => {
    if (!file) return;

    setLoading(true);
    setError(null);

    try {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const content = e.target.result;

          // Check file type and parse accordingly
          if (file.name.endsWith('.json')) {
            const data = JSON.parse(content);

            // Validate and extract data
            const customerId = data.customerId || data.context?.customer?.toLowerCase().replace(/\s+/g, '-');
            const contextData = data.context || data.contextData || {};
            const processes = data.processes || [];

            // Build upload payload - only include non-empty data
            const uploadPayload = {};

            if (customerId) {
              uploadPayload.customerId = customerId;
            }

            // Only include context if it has customer data
            if (contextData && contextData.customer && contextData.customer.trim() !== '') {
              uploadPayload.contextData = contextData;
            }

            // Only include processes if array has items
            if (processes && processes.length > 0) {
              uploadPayload.processes = processes;
            }

            if (onFilesUploaded && Object.keys(uploadPayload).length > 0) {
              onFilesUploaded(uploadPayload);
            }

            setLoading(false);
          } else if (file.name.endsWith('.csv')) {
            // Parse CSV file
            const processes = parseCSV(content);

            // Enrich processes with calculated fields
            const enrichedProcesses = sortAndRenumberProcesses(processes);

            // Generate customer ID from first process if available
            const customerId = processes[0]?.name ?
              processes[0].name.toLowerCase().replace(/\s+/g, '-').slice(0, 20) :
              'imported-processes';

            // Build upload payload - only include processes (CSV doesn't have context)
            const uploadPayload = {
              customerId
            };

            // Only include processes if array has items
            if (enrichedProcesses && enrichedProcesses.length > 0) {
              uploadPayload.processes = enrichedProcesses;
            }

            if (onFilesUploaded && uploadPayload.processes) {
              onFilesUploaded(uploadPayload);
            }

            setLoading(false);
          } else {
            setError('Unsupported file format. Please upload JSON or CSV files.');
            setLoading(false);
          }
        } catch (err) {
          console.error('Error parsing file:', err);
          setError(`Failed to parse file: ${err.message}`);
          setLoading(false);
        }
      };

      reader.onerror = () => {
        setError('Failed to read file');
        setLoading(false);
      };

      reader.readAsText(file);
    } catch (err) {
      console.error('Error handling file:', err);
      setError(`Error handling file: ${err.message}`);
      setLoading(false);
    }
  };

  return (
    <Paper shadow="sm" p="xl" radius="md">
      <Stack gap="md">
        <Title order={2} c="purple">Getting Started</Title>

        <Text c="dimmed">
          Upload a JSON or CSV file containing customer context and process data to get started.
        </Text>

        <Alert color="gray" title="Quick Start Guide">
          <Text size="sm">
            <strong>1. Upload Files</strong> - Upload your customer context (JSON) and process list (CSV) below<br/>
            <strong>2. Review Context</strong> - Verify customer information and customize the report introduction<br/>
            <strong>3. Rate Processes</strong> - Use AI or manually rate processes across impact and complexity dimensions<br/>
            <strong>4. Configure ROI</strong> - Set ROI parameters for each process (automation area, potential, interactions)<br/>
            <strong>5. Generate Report</strong> - Download the complete Word document with charts and analysis
          </Text>
        </Alert>

        <Alert color="cyan" title="Tips">
          <Text size="sm">
            • You can upload files individually on their respective tabs<br/>
            • Context and processes are auto-saved when files are uploaded<br/>
            • Use the "Customer Context" tab to download a template JSON file<br/>
            • CSV format: First row should contain "Process ID" and "Process Name" columns
          </Text>
        </Alert>

        <Group>
          <FileButton onChange={handleFileUpload} accept=".json,.csv">
            {(props) => (
              <Button
                {...props}
                leftSection={<IconUpload size={16} />}
                color="purple"
                loading={loading}
                disabled={loading}
              >
                Upload File
              </Button>
            )}
          </FileButton>
        </Group>

        {error && (
          <Alert color="red" title="Error">
            {error}
          </Alert>
        )}
      </Stack>
    </Paper>
  );
}

export default GettingStarted;